import { GetApp, InfoOutlined, UploadFile } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { DataImportService } from "../../../Services/DataImportService";
import { GET } from "../../../Utilities/BaseService"; // Corrected import path for GET

// Helper function to read Excel file and create collections
const readExcelFileAndCreateCollections = async (
    bin: string | ArrayBuffer | null | undefined
): Promise<any[]> => {
    console.log("in readExcelFileAndCreateCollections");
    const wb = XLSX.read(bin, { type: "binary" });

    const sheets = wb.Sheets;

    let collectionFailures: any[] = [],
        collectionSuccesses: any[] = [];

    // Loop through each sheet in the workbook
    for (const sheet of Object.values(sheets)) {
        // Convert sheet to JSON and format dates
        const json = XLSX.utils.sheet_to_json(sheet, {
            raw: false, // Ensures dates are parsed as strings, not raw numbers
            dateNF: "yyyy-mm-dd", // Desired format for dates
        });

        const parsedJson = json.map((row: any) => {
            // Further ensure collectionDate is in ISO string format if it was a numeric date
            if (row.collectionDate && typeof row.collectionDate === "number") {
                row.collectionDate = XLSX.SSF.format(
                    "yyyy-mm-dd",
                    row.collectionDate
                );
            }
            return row;
        });

        // Call service to create reading collections
        const [data, err] = await DataImportService.createReadingCollections(
            parsedJson
        );

        console.log("data", data, "error", err);

        // Populate successes and failures based on service response
        if (data) {
            collectionSuccesses = [
                ...collectionSuccesses,
                ...(data.successes || []),
            ];
            collectionFailures = [
                ...collectionFailures,
                ...(data.failures || []),
            ];
        } else if (err) {
            collectionSuccesses = [
                ...collectionSuccesses,
                ...(err.successes || []),
            ];
            collectionFailures = [
                ...collectionFailures,
                ...(err.failures || []),
            ];
        }
    }

    return [collectionSuccesses, collectionFailures];
};

export const DataImport = () => {
    // State for loading status: 'initial', 'loading', 'finished'
    const [loading, setLoading] = useState<"loading" | "initial" | "finished">(
        "initial"
    );
    // State for the name of the selected file
    const [fname, setFname] = useState("");

    // RENAMED: paymentMethod -> paymentMethods (as it's an array)
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);

    // State for successful and failed collection imports
    const [successes, setSucesses] = useState<any[]>([]);
    const [failures, setFailures] = useState<any[]>([]);
    // State to control visibility of response details (successes/failures list)
    const [showResponseDetails, setShowResponseDetails] = useState(false);
    // State to hold the selected file object
    const [file, setFile] = useState<File | null>(null);
    // NEW STATE: State to control the visibility of the "no file selected" alert
    const [showNoFileAlert, setShowNoFileAlert] = useState(false);

    // Handler for file upload process
    const handleFileUpload = async () => {
        // NEW LOGIC: Check if a file is selected
        if (!file) {
            setShowNoFileAlert(true); // Show the alert if no file
            return; // Stop the function execution
        }

        setShowNoFileAlert(false); // Hide the alert if a file is selected and we proceed

        setFname(file.name); // Set the file name for display
        const reader = new FileReader(); // Create a new FileReader instance

        setLoading("loading"); // Set loading state to indicate processing

        // Define what happens when the file is done loading
        reader.onload = async (evt) => {
            const binary = evt.target?.result; // Get the file content as binary string

            // Read the Excel data and create collections
            const [_successes, _failures] =
                await readExcelFileAndCreateCollections(binary);

            console.log("_successes", _successes, "_failures", _failures);

            setFailures(_failures); // Update failures state
            setSucesses(_successes); // Update successes state

            setLoading("finished"); // Set loading state to finished
        };

        reader.readAsBinaryString(file); // Read the file as a binary string
    };

    // RENAMED: getPaymentMethod -> getPaymentMethods (as it fetches multiple)
    const getPaymentMethods = async () => {
        try {
            const res = await GET("/reading/payment-method", {
                page: 1,
                limit: 1000,
            });

            if (res.data && res.data.rows && res.data.rows.length > 0) {
                setPaymentMethods(res.data.rows); // Set the array of payment methods
            }
        } catch (error) {
            console.error("Failed to fetch payment methods:", error);
            // Optionally set an error state here to inform the user
        }
    };

    useEffect(() => {
        getPaymentMethods();
    }, []);

    return (
        <Card>
            <CardContent>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2} // Margin bottom for spacing
                >
                    <Typography variant="h5" fontWeight="bold">
                        Import Reading Collections
                    </Typography>

                    <Box display={"flex"} gap={1}>
                        {/* Upload File Button */}
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={loading === "loading"}
                            startIcon={
                                loading === "loading" ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <UploadFile />
                                )
                            }
                            onClick={handleFileUpload} // Call the handler directly
                        >
                            Upload File
                        </Button>
                        {/* Download Template Button */}
                        <Button
                            variant="outlined"
                            startIcon={<GetApp />}
                            href="/template/CollectionTemplate.xlsx" // Link to your template file
                            download // Prompt download
                            color="secondary"
                        >
                            Download Template
                        </Button>
                    </Box>
                </Box>

                <div>
                    {/* File Input */}
                    <div>
                        <input
                            onChange={(e) => {
                                setFile(
                                    e.target.files?.length
                                        ? e.target.files[0]
                                        : null
                                );
                                // NEW LOGIC: Hide the "no file" alert when a file is chosen/deselected
                                setShowNoFileAlert(false);
                            }}
                            type="file"
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            style={{ marginBottom: "10px" }} // Small style adjustment for spacing
                        />
                        {fname && ( // Display selected file name if available
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: 1 }}
                            >
                                Selected file: <strong>{fname}</strong>
                            </Typography>
                        )}
                    </div>

                    {/* NEW: "No File Selected" Alert - IMPROVED MESSAGE */}
                    {showNoFileAlert && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <AlertTitle>No File Selected</AlertTitle>
                            Please choose an Excel file (.xlsx) using the file
                            input above before clicking 'Upload File'.
                        </Alert>
                    )}

                    {/* Instructions and Hints - REFORMATTED AND MADE MEANINGFUL */}
                    <Box
                        sx={{
                            mt: 3,
                            mb: 3,
                            p: 2,
                            border: "1px dashed #ccc",
                            borderRadius: "8px",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            File Upload Guidelines
                        </Typography>

                        <Typography variant="body1" paragraph>
                            To ensure a successful data import, please adhere to
                            the following guidelines for your Excel file:
                        </Typography>

                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                        >
                            1. File Format and Column Names:
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>
                            Your file must be in{" "}
                            <Box
                                component="span"
                                fontWeight="bold"
                                color="primary.main"
                            >
                                .xlsx
                            </Box>{" "}
                            format. The Excel sheet should contain the following
                            exact column headers:
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ ml: 4, mt: 0, mb: 2, listStyleType: "disc" }}
                        >
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    amount
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    paymentMethod
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    meterNo
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    collectionDate
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    description
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.5 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    adjustment
                                </Box>
                            </Typography>
                        </Box>

                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                        >
                            2. Data Format Requirements:
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ ml: 4, mt: 0, mb: 2, listStyleType: "disc" }}
                        >
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 1 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    Collection Date:
                                </Box>{" "}
                                Must be in{" "}
                                <Box component="span" fontStyle="italic">
                                    ISO format (YYYY-MM-DD)
                                </Box>
                                , for example,{" "}
                                <Box component="span" fontWeight="bold">
                                    "2025-02-28"
                                </Box>
                                .
                                <br />
                                <Box
                                    component="span"
                                    color="error.main"
                                    fontStyle="italic"
                                >
                                    Incorrect date formats may lead to
                                    processing errors.
                                </Box>
                            </Typography>
                            <Typography
                                component="li"
                                variant="body2"
                                sx={{ mb: 1 }}
                            >
                                <Box component="span" fontWeight="bold">
                                    Payment Method:
                                </Box>{" "}
                                Must be a valid{" "}
                                <Box component="span" fontWeight="bold">
                                    ID
                                </Box>
                                , for example,{" "}
                                <Box component="span" fontWeight="bold">
                                    1
                                </Box>
                                .
                                <br />
                                <Box
                                    component="span"
                                    color="error.main"
                                    fontStyle="italic"
                                >
                                    Using an incorrect payment method ID may
                                    result in processing errors.
                                </Box>
                            </Typography>
                        </Box>

                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                        >
                            3. How to Find Payment Method IDs (Hint):
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            You can find the correct Payment Method IDs and
                            their corresponding names below. Use the ID in your
                            Excel file.
                        </Typography>
                        {/* CONDITIONAL RENDERING OF PAYMENT METHODS */}
                        {paymentMethods.length > 0 ? (
                            <Box
                                component="ul"
                                sx={{
                                    ml: 4,
                                    mt: 1,
                                    mb: 0,
                                    listStyleType: "decimal",
                                }}
                            >
                                {paymentMethods.map((method) => (
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        key={method.id}
                                        sx={{ mb: 0.5 }}
                                    >
                                        ID:{" "}
                                        <Box component="span" fontWeight="bold">
                                            {method.id}
                                        </Box>{" "}
                                        (Name: {method.name})
                                    </Typography>
                                ))}
                            </Box>
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{ ml: 4, mt: 1, color: "text.secondary" }}
                            >
                                Loading payment methods or none available...
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ ml: 2, mt: 2 }}>
                            Alternatively, you can navigate to the sidebar,
                            click on "Payments", then "Payment Method" to view
                            the full list.
                        </Typography>
                    </Box>

                    {/* Linear Progress Bar during loading */}
                    {loading === "loading" && <LinearProgress sx={{ mt: 2 }} />}

                    {/* Section to display results (successes/failures) */}
                    <section
                        hidden={loading !== "finished"}
                        style={{ marginTop: "20px" }}
                    >
                        <Alert
                            severity="info"
                            action={
                                <IconButton
                                    onClick={() => {
                                        setShowResponseDetails(
                                            !showResponseDetails
                                        );
                                    }}
                                >
                                    <InfoOutlined fontSize="small" />
                                </IconButton>
                            }
                        >
                            <AlertTitle>
                                {`${successes?.length} successes out of ${
                                    (successes?.length ?? 0) +
                                    (failures?.length ?? 0)
                                } `}
                            </AlertTitle>

                            {/* Collapse to show/hide detailed results */}
                            <Collapse in={showResponseDetails}>
                                {successes?.length > 0 && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ mt: 1, mb: 0.5 }}
                                    >
                                        Successful Imports:
                                    </Typography>
                                )}
                                {successes?.map(
                                    (
                                        suc: {
                                            message: string;
                                            meterNo: number | string;
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            key={`success-${index}`}
                                            sx={{ fontSize: "0.85rem" }}
                                        >
                                            {`Meter No - (${suc.meterNo}) - ${suc.message}`}
                                        </Box>
                                    )
                                )}

                                {failures.length > 0 && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            mt: 1,
                                            mb: 0.5,
                                            color: "error.main",
                                        }}
                                    >
                                        Failed Imports:
                                    </Typography>
                                )}
                                {failures.map(
                                    (
                                        err: {
                                            message: string;
                                            meterNo: number | string;
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            key={`failure-${index}`}
                                            sx={{
                                                fontSize: "0.85rem",
                                                color: "error.main",
                                            }}
                                        >
                                            {`Meter No - (${err.meterNo}) - ${err.message}`}
                                        </Box>
                                    )
                                )}
                            </Collapse>
                        </Alert>
                    </section>
                </div>
            </CardContent>
        </Card>
    );
};

export default DataImport;
